/**
 * RefundSummary -- line items showing the refund breakdown.
 *
 * Green text for refund amount, red for no refund.
 */
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import type { CancellationPolicy } from '@/lib/types/cancellation.types'
import { formatCurrency } from '@/lib/utils/format'

interface RefundSummaryProps {
  refundCents: number
  cancellationFeeCents: number
  policy: CancellationPolicy
}

export function RefundSummary({ refundCents, cancellationFeeCents, policy }: RefundSummaryProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const refundColor = policy === 'none' ? colors.error : colors.success

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        padding: SPACING.md,
      }}
    >
      <Text
        style={{
          fontSize: 18,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          marginBottom: SPACING.md,
        }}
      >
        {t('cancellation.refund_summary_title')}
      </Text>

      {/* Refund line */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: SPACING.sm,
        }}
      >
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {t('cancellation.refund_line')}
        </Text>
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.bodyBold,
            color: refundColor,
          }}
        >
          {refundCents > 0 ? formatCurrency(refundCents) : t('cancellation.no_refund')}
        </Text>
      </View>

      {/* Fee line */}
      {cancellationFeeCents > 0 && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: SPACING.sm,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('cancellation.cancellation_fee')}
          </Text>
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textError,
            }}
          >
            {formatCurrency(cancellationFeeCents)}
          </Text>
        </View>
      )}

      {/* Divider + total */}
      <View
        style={{
          height: 1,
          backgroundColor: colors.border,
          marginVertical: SPACING.sm,
        }}
      />
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text
          style={{
            fontSize: 16,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('cancellation.refund_total')}
        </Text>
        <Text
          style={{
            fontSize: 20,
            fontFamily: FONTS.bodyBold,
            color: refundColor,
          }}
        >
          {refundCents > 0 ? formatCurrency(refundCents) : t('cancellation.no_refund')}
        </Text>
      </View>
    </View>
  )
}
